<template>
  <div ref="questionsForm">
    <div class="comment-form-info-message" v-if="!isSubmitting && message">
      <InfoMessage :type="message.success ? 'info' : 'error'" :title="message.message" centered />
    </div>
    <form v-else
          enctype="multipart/form-data"
          class="comment-form"
          :class="{ 'comment-form--answer': isAnswer }">
      <div class="comment-form__item" v-if="!isAnswer">
        <div class="comment-form__item-label">{{ $t('questions.add_tag') }}</div>
        <div class="comment-form__item-sublabel">{{ $t('questions.add_tag_info') }}</div>
        <div class="comment-form__tags">
          <label v-for="tag in tags" :key="tag.id" class="comment-form__tag" :class="{'active': selectedTags.includes(tag.id)}">
            <input type="checkbox" :value="tag.id" v-model="selectedTags">
            <span class="comment-form__tag-title">{{ tag.title }}</span>
          </label>
        </div>
        <p class="control-box__help-block invalid dirty comment-form__tags-error" v-if="tagsError">
          {{ $t('questions.tags_error') }}
        </p>
      </div>
      <div class="comment-form__item comment-form__questions-item">
        <CustomTextarea
          name="comment"
          :placeholder="$t('questions.textarea_placeholder')"
          :label="isAnswer ? '' : $t('questions.textarea_label')"
          v-model="textarea.comment"
          :is-over="overText.comment"
          :is-error="errors.comment"
          @input="handleComment($event, 'comment')"
          :maxlength="2000"
        />
        <span
          class="comment-form__comment-max-symbols"
          :class="{ 'comment-form__comment-max-symbols--without-offset': overText.comment }"
        ><span :class="{ 'is-over': overText.comment }">{{ currentLength.comment }}</span> /
          {{ maxLength.comment }}</span
        >
      </div>
      <FileInput
        :mainText="$t('questions.add_photo')"
        :secondaryText="$t('review_add_photo_text')"
        @changeFiles="images = $event"
        ref="fileInput"
        name="images"
        :formats="['jpg', 'jpeg', 'png', 'gif']"
        :max-file-size="5242880"
        :max-files-count="10"
        class="comment-form__item"
        v-if="!isAnswer"
      />
      <VideoInput @changeVideos="videos = $event"
                  ref="videoInput"
                  class="comment-form__item"
                  :is-questions="true"
                  v-if="!isAnswer" />
      <div class="comment-form__btn-wrap">
        <Button v-if="isAnswer" type="gray" @click.native="hideAnswerForm">
          {{ $t('comment.form.cancel') }}
        </Button>
        <Button
          class="comment-form__btn"
          @click.native="submitComment"
          :is-disabled="isSubmitting || tagsError"
          :is-native-disabled="isSubmitting || tagsError"
        >
          {{ $t('questions.submit') }}
        </Button>
      </div>
    </form>
  </div>
</template>

<script>
import { mapState, mapActions } from 'vuex'
import CustomTextarea from '@/components/inputs/CustomTextarea'
import Button from '@/components/inputs/Button'
import FileInput from '../inputs/FileInput'
import VideoInput from '@/components/inputs/VideoInput'
import InfoMessage from '../helpers/InfoMessage'

export default {
  name: 'QuestionsForm',
  data () {
    return {
      tagsError: false,
      images: [],
      videos: [],
      selectedTags: [],
      textarea: {
        comment: ''
      },
      overText: {
        comment: false
      },
      currentLength: {
        comment: 0
      },
      maxLength: {
        comment: 2000
      },
      errors: {
        comment: false
      },
      isSubmitting: false,
      message: null
    }
  },
  components: {
    VideoInput,
    Button,
    CustomTextarea,
    FileInput,
    InfoMessage
  },
  props: {
    parentId: {
      type: Number,
      default: null
    },
    productId: {
      type: Number,
      default: null
    },
    type: {
      type: String,
      required: true
    },
    endpoint: {
      type: String,
      required: true
    }
  },
  computed: {
    ...mapState({
      // productId: state => state.productInfo.productInfo.productId
      tags: state => state.questions.tags
    }),
    isAnswer () {
      return this.type === this.$constants.COMMENTS.FORM_TYPE_ANSWER
    },
    isProductComments () {
      return this.type === this.$constants.COMMENTS.FORM_TYPE_PRODUCT
    },
    isCompanyComments () {
      return this.type === this.$constants.COMMENTS.FORM_TYPE_COMPANY
    },
    isValidComment () {
      const spaces = this.textarea.comment.replace(/^\s+|\s+$/g, '')

      return (
        this.textarea.comment &&
        !!spaces &&
        this.currentLength.comment <= this.maxLength.comment &&
        !this.overText.comment
      )
    }
  },
  methods: {
    ...mapActions('questions', ['POST_NEW_QUESTION']),
    hideAnswerForm () {
      this.$emit('hideAnswerForm')
    },
    handleComment (value, el) {
      const lineBreaks = (value.match(/\n/g) || []).length

      this.currentLength[el] = this.textarea[el].length + lineBreaks

      this.overText[el] = this.currentLength[el] > this.maxLength[el]

      this.textarea[el] = value
    },
    getFormData () {
      let data = new FormData()

      if (this.parentId) data.append('parentId', this.parentId)
      if (this.productId) data.append('productId', this.productId)
      if (this.textarea.comment) data.append('comment', this.textarea.comment)

      this.selectedTags.forEach(tag => {
        data.append('tags[]', tag)
      })

      this.videos.forEach(video => {
        data.append('videos[]', video)
      })

      this.images.forEach(file => {
        data.append('images[]', file)
      })

      return data
    },
    clearForm () {
      this.textarea = {
        comment: ''
      }
      this.tags = []

      this.message = null

      if (this.$refs.fileInput) this.$refs.fileInput.clearFiles()
      if (this.$refs.videoInput) this.$refs.videoInput.clearAll()
    },
    submitComment () {
      const formData = this.getFormData()
      const isValidComment = this.isValidComment

      this.errors.comment = !isValidComment

      if (isValidComment) {
        this.isSubmitting = true

        this.POST_NEW_QUESTION({ formData, endpoint: this.endpoint })
          .then(res => {
            this.message = res
          })
          .catch(err => {
            this.message = err
          })
          .finally(() => {
            this.isSubmitting = false
            this.$emit('submitted')
            const top = this.$refs.questionsForm.offsetTop
            window.scrollTo({
              top: top
            })
          })
      }
    }
  },
  destroyed () {
    this.clearForm()
  },
  watch: {
    selectedTags (val) {
      this.tagsError = val.length > 2
    }
  }
}
</script>

<style scoped lang="sass">
.comment-form
  &__tag
    font-size: 13px
    font-weight: 400
    line-height: 16px
    padding: 7px 15px
    cursor: pointer
    color: #3E77AA
    margin: 0 10px 10px 0
    border: 1px solid rgba(#3E77AA, 0.5)
    border-radius: 5px
    &.active
      background-color: rgba(#3E77AA, 0.1)
      border-color: transparent

  &__tags
    display: flex
    flex-wrap: wrap
    margin-bottom: -10px
    &-error
      margin-top: 5px
      margin-left: 0
.comment-form__item
  margin-bottom: 30px
  position: relative
  &-label
    font-size: 18px
    font-weight: 500
    line-height: 23px
    margin-bottom: 10px
  &-sublabel
    font-size: 14px
    font-weight: 400
    line-height: 18px
    margin-bottom: 15px

.comment-form__star-wrap
  display: flex
  align-items: center
  flex-flow: row wrap
  margin: 40px 0

.comment-form__star-label
  font-size: 14px
  margin-right: 30px

.comment-form__btn-wrap
  display: flex
  flex-flow: row nowrap
  align-items: center
  justify-content: center
  margin-bottom: 30px

.comment-form__comment-max-symbols
  display: block
  width: 100%
  color: $placeholder-color
  font-size: 12px
  font-weight: 400
  text-align: right
  position: absolute
  bottom: -15px

  &--without-offset
    bottom: 1px

.is-over
  color: $main-color

.comment-form--answer
  .comment-form__star-wrap
    margin-bottom: 20px

  .comment-form__btn-wrap
    justify-content: flex-end

  .comment-form__btn
    margin-left: 20px

.comment-form-info-message
  margin-bottom: 20px
</style>
